/* eslint-disable no-useless-escape */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Plus,
  Bot,
  Pencil,
  Trash2,
  Copy,
  History,
  Globe,
  Code,
  ChevronDown,
  ChevronRight,
  Zap,
  Check,
  X,
  Search,
  ArrowLeft,
  RotateCcw,
  EyeOff,
  Lock,
  Users,
  AlertCircle,
  FileText,
  Upload,
  Download,
  Wand2
} from 'lucide-react';
import { apiClient } from '../apiClient';
import Modal from '../components/Modal';
import SelectMenu from '../components/SelectMenu';
import VisibilityIndicator from '../components/VisibilityIndicator';

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private', icon: Lock, description: 'Only you can see this' },
  { value: 'public', label: 'Public', icon: Globe, description: 'Visible in the library' },
  { value: 'shared', label: 'Shared', icon: Users, description: 'Only people you share with' }
];

const INPUT_TYPE_OPTIONS = [
  { value: 'text', label: 'Text', description: 'Single line text input' },
  { value: 'multi_text', label: 'Multi-line Text', description: 'Paragraph or long text' },
  { value: 'dropdown', label: 'Dropdown', description: 'Select one from a list' },
  { value: 'multiple_choice', label: 'Multiple Choice', description: 'Select multiple options' },
  { value: 'number', label: 'Number', description: 'Numeric input' }
];

const INSTRUCTION_SOURCE_LABELS = {
  claude_md: 'Claude Code instruction file',
  agents_md: 'Agent instruction file',
  codex_skill_md: 'Codex skill file',
  markdown: 'Markdown instruction file',
  file: 'Imported instruction file'
};

const MARKDOWN_EXPORT_TARGETS = [
  {
    value: 'claude',
    label: 'CLAUDE.md',
    description: 'Claude Code project memory format.',
    filename: 'CLAUDE.md',
    sourceType: 'claude_md',
    sourceLabel: INSTRUCTION_SOURCE_LABELS.claude_md,
    downloadHint: 'Claude Code looks for a file named CLAUDE.md.'
  },
  {
    value: 'agents',
    label: 'AGENTS.md',
    description: 'Shared agent workspace instructions.',
    filename: 'AGENTS.md',
    sourceType: 'agents_md',
    sourceLabel: INSTRUCTION_SOURCE_LABELS.agents_md,
    downloadHint: 'OpenClaw and similar tools expect AGENTS.md as the workspace instruction file.'
  },
  {
    value: 'skill',
    label: 'SKILL.md',
    description: 'Agent skill / playbook format.',
    filename: 'SKILL.md',
    sourceType: 'codex_skill_md',
    sourceLabel: INSTRUCTION_SOURCE_LABELS.codex_skill_md,
    downloadHint: 'Skill-based tools typically expect SKILL.md inside a skill folder.'
  },
  {
    value: 'general',
    label: 'General .md',
    description: 'Portable Markdown instructions.',
    filename: 'agent-instructions.md',
    sourceType: 'markdown',
    sourceLabel: INSTRUCTION_SOURCE_LABELS.markdown,
    downloadHint: 'Use any .md filename you want for general-purpose sharing.'
  }
];

const inferInstructionSource = (filename = '') => {
  const normalized = String(filename || '').trim().split(/[\\/]/).pop() || '';
  const lower = normalized.toLowerCase();

  if (!normalized) {
    return { type: '', filename: '', label: '' };
  }
  if (lower === 'claude.md') {
    return { type: 'claude_md', filename: normalized, label: INSTRUCTION_SOURCE_LABELS.claude_md };
  }
  if (lower === 'agents.md') {
    return { type: 'agents_md', filename: normalized, label: INSTRUCTION_SOURCE_LABELS.agents_md };
  }
  if (lower === 'skill.md' || lower === 'skills.md') {
    return { type: 'codex_skill_md', filename: normalized, label: INSTRUCTION_SOURCE_LABELS.codex_skill_md };
  }
  if (lower.endsWith('.md')) {
    return { type: 'markdown', filename: normalized, label: INSTRUCTION_SOURCE_LABELS.markdown };
  }
  return { type: 'file', filename: normalized, label: INSTRUCTION_SOURCE_LABELS.file };
};

const humanizeFilename = (filename = '') => {
  return String(filename || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const inferInstructionNameFromContent = (filename, content) => {
  const headingMatch = String(content || '').match(/^\s*#\s+(.+?)\s*$/m);
  const heading = headingMatch?.[1]?.trim();
  if (heading) return heading;

  const fallbackName = humanizeFilename(filename);
  if (fallbackName) return fallbackName;
  return 'Imported Instruction';
};

const inferInstructionDescriptionFromContent = (content) => {
  const lines = String(content || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    if (line.startsWith('#') || line.startsWith('```')) continue;
    if (line.length < 24) continue;
    return line.length > 180 ? `${line.slice(0, 177).trim()}...` : line;
  }
  return '';
};

const slugifyFileStem = (value = '') => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'agent-instructions';
};

const stripMarkdownLinePrefix = (line = '') => {
  return String(line || '')
    .replace(/^\s*[-*•]\s+/, '')
    .replace(/^\s*\d+[\).\-\s]+\s*/, '')
    .trim();
};

const dedupeList = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const normalized = String(item || '').trim();
    if (!normalized) return false;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildBulletSection = (title, items) => {
  const list = dedupeList(items);
  if (!list.length) return '';
  return `## ${title}\n${list.map((item) => `- ${item}`).join('\n')}`;
};

const buildNumberedSection = (title, items) => {
  const list = dedupeList(items);
  if (!list.length) return '';
  return `## ${title}\n${list.map((item, index) => `${index + 1}. ${item}`).join('\n')}`;
};

const classifyInstructionDraft = (draft, variables, name, description) => {
  const lines = String(draft || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const overview = [];
  const role = [];
  const workflow = [];
  const guardrails = [];
  const outputs = [];
  const context = [];

  lines.forEach((line) => {
    const normalized = stripMarkdownLinePrefix(line);
    if (!normalized) return;
    const lower = normalized.toLowerCase();
    const isNumbered = /^\d+[\).\-\s]+/.test(line);

    if (/^(you are|act as|your role|role:|persona:|identity:)/i.test(normalized)) {
      role.push(normalized);
      return;
    }
    if (isNumbered || /^(step|first|then|next|finally|after|before)\b/i.test(lower)) {
      workflow.push(normalized);
      return;
    }
    if (/(must|never|do not|don't|always|only|avoid|forbidden|required|under no circumstances|no )/.test(lower)) {
      guardrails.push(normalized);
      return;
    }
    if (/(output|return|respond|deliver|format|write|final answer|final output)/.test(lower)) {
      outputs.push(normalized);
      return;
    }
    if (/(context|background|reference|input|provided|source material|knowledge)/.test(lower)) {
      context.push(normalized);
      return;
    }
    overview.push(normalized);
  });

  const variableLines = (variables || []).map((variable) => {
    const label = variable.display_name || variable.variable_name;
    const base = `\`${variable.variable_name}\`${label && label !== variable.variable_name ? ` (${label})` : ''}`;
    const details = [];
    if (variable.description) details.push(variable.description);
    if (variable.default_value) details.push(`Default: ${variable.default_value}`);
    return details.length ? `${base}: ${details.join(' ')}` : base;
  });

  const summary = description?.trim() || overview[0] || role[0] || 'Follow these instructions consistently.';

  return {
    title: name?.trim() || 'Agent Instructions',
    summary,
    overview,
    role,
    workflow,
    guardrails,
    outputs,
    context,
    variableLines
  };
};

const renderMarkdownForTarget = (target, sections, rawDraft) => {
  const title = sections.title;
  const summary = sections.summary;
  const blocks = [];

  if (target === 'skill') {
    const skillName = slugifyFileStem(title).replace(/-/g, '_');
    blocks.push('---');
    blocks.push(`name: ${skillName}`);
    blocks.push(`description: ${summary}`);
    blocks.push('---');
    blocks.push('');
    blocks.push(`# ${title}`);
    blocks.push(summary);
    blocks.push('');
    blocks.push(buildBulletSection('Job To Be Done', sections.overview.length ? sections.overview : [summary]));
    blocks.push(buildBulletSection('Required Inputs', sections.variableLines.length ? sections.variableLines : sections.context));
    blocks.push(buildNumberedSection('Workflow', sections.workflow.length ? sections.workflow : sections.overview));
    blocks.push(buildBulletSection('Output Format', sections.outputs));
    blocks.push(buildBulletSection('Final Checks', sections.guardrails));
  } else if (target === 'agents') {
    blocks.push(`# ${title}`);
    blocks.push(summary);
    blocks.push('');
    blocks.push(buildBulletSection('Role', sections.role.length ? sections.role : [`Use this workspace guide for ${title.toLowerCase()}.`]));
    blocks.push(buildBulletSection('Standing Instructions', sections.overview));
    blocks.push(buildNumberedSection('Workflow', sections.workflow));
    blocks.push(buildBulletSection('Guardrails', sections.guardrails));
    blocks.push(buildBulletSection('Context And Inputs', sections.context.length ? sections.context : sections.variableLines));
    blocks.push(buildBulletSection('Output Expectations', sections.outputs));
  } else if (target === 'general') {
    blocks.push(`# ${title}`);
    blocks.push(summary);
    blocks.push('');
    blocks.push(buildBulletSection('Overview', sections.overview));
    blocks.push(buildBulletSection('Role', sections.role));
    blocks.push(buildNumberedSection('Workflow', sections.workflow));
    blocks.push(buildBulletSection('Guardrails', sections.guardrails));
    blocks.push(buildBulletSection('Variables', sections.variableLines));
    blocks.push(buildBulletSection('Output Expectations', sections.outputs));
    blocks.push(buildBulletSection('Reference Context', sections.context));
  } else {
    blocks.push(`# ${title}`);
    blocks.push(summary);
    blocks.push('');
    blocks.push(buildBulletSection('Core Role', sections.role.length ? sections.role : [`Apply the following project memory for ${title.toLowerCase()}.`]));
    blocks.push(buildBulletSection('Instructions', sections.overview));
    blocks.push(buildBulletSection('Guardrails', sections.guardrails));
    blocks.push(buildNumberedSection('Workflow', sections.workflow));
    blocks.push(buildBulletSection('Context', sections.context));
    blocks.push(buildBulletSection('Variables', sections.variableLines));
    blocks.push(buildBulletSection('Output Expectations', sections.outputs));
  }

  const compacted = blocks.filter(Boolean).join('\n\n').trim();
  return compacted || String(rawDraft || '').trim() || `# ${title}`;
};

// List view
function AgentInstructionsList() {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/agent-instructions');
      setInstructions(data || []);
    } catch (err) {
      console.error('Failed to load instructions:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return instructions;
    const term = search.toLowerCase();
    return instructions.filter(
      (i) =>
        i.name?.toLowerCase().includes(term) ||
        i.description?.toLowerCase().includes(term)
    );
  }, [instructions, search]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="text-3xl font-bold">Agent Instructions</h1>
            <p className="text-gray-400 text-sm mt-1">
              Build dynamic instructions for AI agents. Pull them via API without hardcoding.
            </p>
          </div>
          <div className="page-header-actions">
            <div className="search-field">
              <Search className="w-4 h-4 opacity-60" />
              <input
                type="text"
                placeholder="Search instructions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className="btn-primary"
              onClick={() => navigate('/agent-instructions/create')}
            >
              <Plus className="w-4 h-4" />
              New Instruction
            </button>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card-glass text-center py-12">
          <Bot className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <h3 className="text-xl font-bold mb-2">No Instructions Yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Create your first agent instruction. Your AI agents can fetch these dynamically via API,
            so you never need to hardcode instructions again.
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate('/agent-instructions/create')}
          >
            <Plus className="w-4 h-4" />
            Create Your First Instruction
          </button>
        </div>
      ) : (
        <div className="tile-grid">
          {filtered.map((instruction) => (
            <div
              key={instruction.instruction_id}
              className="tile-card cursor-pointer"
              onClick={() => navigate(`/agent-instructions/${instruction.instruction_id}`)}
            >
              <div className="tile-card-header">
                <div className="tile-icon" style={{ background: 'rgba(6, 182, 212, 0.2)' }}>
                  <Bot className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex items-center gap-2">
                  <VisibilityIndicator visibility={instruction.visibility || 'private'} />
                  {instruction.live_enabled && (
                    <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-300 border border-green-500/30" title="Live enabled">
                      Live
                    </span>
                  )}
                  {instruction.dev_enabled && !instruction.live_enabled && (
                    <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-300 border border-yellow-500/30" title="Dev only">
                      Dev
                    </span>
                  )}
                </div>
              </div>
              <div className="tile-title">{instruction.name}</div>
              <div className="tile-sub">
                {instruction.description || 'No description'}
              </div>
              <div className="tile-meta">
                {instruction.variables_json?.length || 0} variables
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Create/Edit form
function AgentInstructionForm({ instructionId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(instructionId);
  const templateRef = useRef(null);
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [sourceFilename, setSourceFilename] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [variables, setVariables] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [projectId, setProjectId] = useState(location.state?.projectId || '');
  const [folderId, setFolderId] = useState(location.state?.folderId || '');
  const [visibility, setVisibility] = useState('private');
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [devEnabled, setDevEnabled] = useState(true);
  const [tags, setTags] = useState([]);

  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [conversionStatus, setConversionStatus] = useState('');
  const [markdownTarget, setMarkdownTarget] = useState('claude');
  const [markdownReady, setMarkdownReady] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('CLAUDE.md');
  const [preConversionTemplate, setPreConversionTemplate] = useState('');
  const fallbackBackTo = projectId && folderId
    ? `/agent-instructions/projects/${projectId}/folders/${folderId}`
    : projectId
      ? `/agent-instructions/projects/${projectId}`
      : '/agent-instructions';
  const backTo = location.state?.backTo || fallbackBackTo;
  const selectedMarkdownTarget = useMemo(
    () => MARKDOWN_EXPORT_TARGETS.find((item) => item.value === markdownTarget) || MARKDOWN_EXPORT_TARGETS[0],
    [markdownTarget]
  );

  useEffect(() => {
    fetchCategories();
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchFolders(projectId);
    } else {
      setFolders([]);
      setFolderId('');
    }
  }, [projectId]);

  useEffect(() => {
    const defaultFilename = selectedMarkdownTarget.filename === 'agent-instructions.md'
      ? `${slugifyFileStem(name || 'agent-instructions')}.md`
      : selectedMarkdownTarget.filename;
    setDownloadFileName(defaultFilename);
  }, [name, selectedMarkdownTarget]);

  // Alt+V / Cmd+V hotkey for adding variables
  const handleAddVariable = useCallback(() => {
    const textarea = templateRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? template.length;
    const end = textarea.selectionEnd ?? template.length;
    const before = template.slice(0, start);
    const after = template.slice(end);
    const placeholder = 'variable_name';
    const nextValue = `${before}{{${placeholder}}}${after}`;
    setTemplate(nextValue);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 2, start + 2 + placeholder.length);
    });
  }, [template]);

  const clearImportedSource = useCallback(() => {
    setSourceType('');
    setSourceFilename('');
    setSourceLabel('');
    setImportStatus('');
    setConversionStatus('');
    setMarkdownReady(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleImportFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name || '';
    if (!/\.md$/i.test(fileName)) {
      setError('Import a Markdown instruction file like CLAUDE.md, AGENTS.md, SKILL.md, or Skills.md.');
      event.target.value = '';
      return;
    }

    try {
      const content = (await file.text()).replace(/\r\n/g, '\n');
      const source = inferInstructionSource(fileName);
      const inferredName = inferInstructionNameFromContent(fileName, content);
      const inferredDescription = inferInstructionDescriptionFromContent(content);

      setTemplate(content);
      setSourceType(source.type);
      setSourceFilename(source.filename);
      setSourceLabel(source.label);
      setImportStatus(`Imported ${source.filename}. The Markdown file is now the instruction template.`);
      setConversionStatus('');
      setMarkdownReady(false);
      setError('');

      if (!name.trim()) {
        setName(inferredName);
      }
      if (!description.trim() && inferredDescription) {
        setDescription(inferredDescription);
      }
      setStep(1);
    } catch (err) {
      setError(err?.message || 'Unable to read the instruction file.');
    } finally {
      event.target.value = '';
    }
  }, [description, name]);

  const handleConvertToMarkdown = useCallback(() => {
    if (!template.trim()) {
      setError('Add plain-English instructions before converting them to Markdown.');
      return;
    }

    const sections = classifyInstructionDraft(template, variables, name, description);
    const nextMarkdown = renderMarkdownForTarget(markdownTarget, sections, template);
    const defaultFilename = selectedMarkdownTarget.filename === 'agent-instructions.md'
      ? `${slugifyFileStem(name || sections.title)}.md`
      : selectedMarkdownTarget.filename;

    setPreConversionTemplate(template);
    setTemplate(nextMarkdown);
    setSourceType(selectedMarkdownTarget.sourceType);
    setSourceFilename(defaultFilename);
    setSourceLabel(selectedMarkdownTarget.sourceLabel);
    setImportStatus('');
    setConversionStatus(`Converted to ${selectedMarkdownTarget.label} using the built-in Markdown formatter.`);
    setMarkdownReady(true);
    setDownloadFileName(defaultFilename);
    setError('');
  }, [description, markdownTarget, name, selectedMarkdownTarget, template, variables]);

  const handleRestorePreConversion = useCallback(() => {
    if (!preConversionTemplate) return;
    setTemplate(preConversionTemplate);
    setConversionStatus('');
    setMarkdownReady(false);
  }, [preConversionTemplate]);

  const handleDownloadMarkdown = useCallback(() => {
    const chosenName = String(downloadFileName || '').trim();
    const finalName = `${chosenName.replace(/\.md$/i, '') || slugifyFileStem(name || 'agent-instructions')}.md`;
    const blob = new Blob([template], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = finalName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setDownloadModalOpen(false);
  }, [downloadFileName, name, template]);

  useEffect(() => {
    const handleHotkey = (event) => {
      if (event.defaultPrevented) return;
      if (step !== 1) return;
      // Alt+V (Windows) or Option+V (Mac)
      if (event.altKey && !event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'v') {
        event.preventDefault();
        handleAddVariable();
      }
    };
    window.addEventListener('keydown', handleHotkey);
    return () => window.removeEventListener('keydown', handleHotkey);
  }, [handleAddVariable, step]);

  // Auto-detect variables from template
  useEffect(() => {
    const pattern = /\{\{([^}]+)\}\}/g;
    const matches = [...template.matchAll(pattern)];
    const detected = [...new Set(matches.map((m) => m[1].trim()))];

    setVariables((prev) => {
      const prevMap = new Map(prev.map((v) => [v.variable_name, v]));
      return detected.map((varName) => {
        const existing = prevMap.get(varName);
        return {
          variable_name: varName,
          display_name: existing?.display_name || formatDisplayName(varName),
          description: existing?.description || '',
          input_type: existing?.input_type || 'text',
          is_required: existing?.is_required ?? true,
          default_value: existing?.default_value || '',
          dropdown_options: existing?.dropdown_options || []
        };
      });
    });
  }, [template]);

  const formatDisplayName = (value) => {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const fetchCategories = async () => {
    try {
      const data = await apiClient.get('/api/categories');
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiClient.get('/api/instruction-projects');
      setProjects(data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchFolders = async (pid) => {
    try {
      const data = await apiClient.get('/api/instruction-folders', { project_id: pid });
      setFolders(data || []);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  };

  const loadInstruction = useCallback(async () => {
    setLoadingData(true);
    try {
      const data = await apiClient.get(`/api/agent-instructions/${instructionId}`);
      setName(data.name || '');
      setDescription(data.description || '');
      setTemplate(data.instruction_template || '');
      setSourceType(data.source_type || '');
      setSourceFilename(data.source_filename || '');
      setSourceLabel(data.source_label || '');
      setConversionStatus('');
      setMarkdownReady(false);
      setCategoryId(data.category_id || '');
      setProjectId(data.project_id || '');
      setFolderId(data.folder_id || '');
      setVisibility(data.visibility || 'private');
      setLiveEnabled(data.live_enabled || false);
      setDevEnabled(data.dev_enabled ?? true);
      setTags(data.tags || []);
      
      const loadedVars = (data.variables || data.variables_json || []).map((v) => ({
        variable_name: v.variable_name,
        display_name: v.display_name || formatDisplayName(v.variable_name),
        description: v.description || '',
        input_type: v.input_type || 'text',
        is_required: v.is_required ?? true,
        default_value: v.default_value || '',
        dropdown_options: v.dropdown_options || []
      }));
      if (loadedVars.length) {
        setVariables(loadedVars);
      }
    } catch (err) {
      setError(err.message || 'Failed to load instruction');
    } finally {
      setLoadingData(false);
    }
  }, [instructionId]);

  useEffect(() => {
    if (isEdit) {
      loadInstruction();
    }
  }, [isEdit, loadInstruction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!name.trim() || !template.trim()) {
        setError('Name and instruction template are required');
        return;
      }
      setError('');
      setStep(2);
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (!projectId || !folderId) {
        setError('Select an instruction project and folder before saving this instruction.');
        setLoading(false);
        return;
      }

      // Clean up dropdown options
      const cleanedVariables = variables.map((v) => {
        const needsOptions = v.input_type === 'dropdown' || v.input_type === 'multiple_choice';
        return {
          ...v,
          dropdown_options: needsOptions 
            ? (v.dropdown_options || []).map((opt) => opt.trim()).filter(Boolean)
            : []
        };
      });

      const payload = {
        name,
        description,
        instruction_template: template,
        source_type: sourceType || null,
        source_filename: sourceFilename || null,
        source_label: sourceLabel || null,
        category_id: categoryId || null,
        project_id: projectId || null,
        folder_id: folderId || null,
        variables: cleanedVariables,
        tags,
        visibility,
        live_enabled: liveEnabled,
        dev_enabled: devEnabled
      };

      if (isEdit) {
        await apiClient.patch(`/api/agent-instructions/${instructionId}`, payload);
        navigate(`/agent-instructions/${instructionId}`, { state: { backTo } });
      } else {
        const created = await apiClient.post('/api/agent-instructions', payload);
        navigate(`/agent-instructions/${created.instruction_id}`, { state: { backTo } });
      }
    } catch (err) {
      setError(err.message || 'Failed to save instruction');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate(backTo)}
            className="text-gray-400 hover:text-white mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Workspace
          </button>
          <h1 className="text-3xl font-bold">
            {isEdit ? 'Edit Instruction' : 'Create Agent Instruction'}
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-sm text-gray-400">Step {step} of 2</p>
            {step === 1 && (
              <p className="text-xs text-gray-500">
                Tip: Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-300">Alt</kbd> + <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-300">V</kbd> to insert a variable
              </p>
            )}
          </div>
          {(projectId || folderId) && (
            <p className="text-xs text-cyan-300 mt-2">
              Organizing in workspace: {projects.find((project) => project.project_id === projectId)?.name || 'Project'} / {folders.find((folder) => folder.folder_id === folderId)?.name || 'Folder'}
            </p>
          )}
        </div>

        {error && <div className="error-banner mb-4">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <>
              <div className="card-glass">
                <h2 className="text-xl font-bold mb-4">Basic Information</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Instruction Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-glass"
                    placeholder="e.g., Customer Support Agent"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="input-glass"
                    rows="2"
                    placeholder="What does this instruction do?"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <SelectMenu
                      value={categoryId}
                      options={categories.map((cat) => ({
                        value: cat.category_id,
                        label: cat.name
                      }))}
                      placeholder="Select category"
                      onChange={setCategoryId}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Visibility</label>
                    <SelectMenu
                      value={visibility}
                      options={VISIBILITY_OPTIONS.map((v) => ({
                        value: v.value,
                        label: v.label
                      }))}
                      onChange={setVisibility}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {VISIBILITY_OPTIONS.find((v) => v.value === visibility)?.description}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Instruction Project *</label>
                    <SelectMenu
                      value={projectId}
                      options={projects.map((project) => ({
                        value: project.project_id,
                        label: project.name
                      }))}
                      placeholder="Select project"
                      onChange={setProjectId}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Instruction Folder *</label>
                    <SelectMenu
                      value={folderId}
                      options={folders.map((folder) => ({
                        value: folder.folder_id,
                        label: folder.name
                      }))}
                      placeholder={projectId ? 'Select folder' : 'Choose a project first'}
                      onChange={setFolderId}
                      disabled={!projectId}
                    />
                  </div>
                </div>
              </div>

              <div className="card-glass">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold">Instruction Template</h2>
                    <p className="text-sm text-gray-400">
                      Write your agent's instructions. Use {'{{variable_name}}'} for dynamic values.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".md,text/markdown"
                      onChange={handleImportFile}
                      className="hidden"
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-compact"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4" />
                      Import `.md`
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-compact"
                      onClick={handleAddVariable}
                    >
                      + Add Variable
                    </button>
                  </div>
                </div>

                <div className="info-banner mb-4">
                  Use <code>CLAUDE.md</code>, <code>AGENTS.md</code>, <code>SKILL.md</code>, or <code>Skills.md</code> as a starting instruction file. The Markdown content will be stored as this instruction template and can still use {'{{variables}}'}.
                </div>

                <div className="markdown-converter-panel">
                  <div className="markdown-converter-copy">
                    <h3>Rule-Based Markdown Conversion</h3>
                    <p>
                      Turn plain-English instructions into a structured Markdown file for Claude, AGENTS, or skill-based tools.
                    </p>
                  </div>
                  <div className="markdown-converter-controls">
                    <div className="markdown-converter-target">
                      <span className="markdown-converter-label">Target Format</span>
                      <SelectMenu
                        value={markdownTarget}
                        options={MARKDOWN_EXPORT_TARGETS}
                        onChange={setMarkdownTarget}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-primary btn-compact"
                      onClick={handleConvertToMarkdown}
                    >
                      <Wand2 className="w-4 h-4" />
                      Convert to Markdown
                    </button>
                    {markdownReady && (
                      <button
                        type="button"
                        className="btn-secondary btn-compact"
                        onClick={() => setDownloadModalOpen(true)}
                      >
                        <Download className="w-4 h-4" />
                        Download .MD File
                      </button>
                    )}
                    {markdownReady && preConversionTemplate && (
                      <button
                        type="button"
                        className="btn-secondary btn-compact"
                        onClick={handleRestorePreConversion}
                      >
                        Restore Plain Text
                      </button>
                    )}
                  </div>
                </div>

                {(sourceFilename || importStatus || conversionStatus) && (
                  <div className="instruction-source-banner">
                    <div className="instruction-source-banner-copy">
                      <div className="instruction-source-banner-title">
                        <FileText className="w-4 h-4" />
                        <span>{sourceLabel || 'Instruction source'}</span>
                      </div>
                      {sourceFilename && (
                        <p className="text-sm text-gray-300">
                          {importStatus ? 'Imported from' : 'Prepared for'} <span className="instruction-source-chip">{sourceFilename}</span>
                        </p>
                      )}
                      {importStatus && <p className="text-xs text-cyan-200/90 mt-1">{importStatus}</p>}
                      {conversionStatus && <p className="text-xs text-cyan-200/90 mt-1">{conversionStatus}</p>}
                    </div>
                    {sourceFilename && (
                      <button
                        type="button"
                        className="btn-secondary btn-compact"
                        onClick={clearImportedSource}
                      >
                        Clear Source
                      </button>
                    )}
                  </div>
                )}

                <textarea
                  ref={templateRef}
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  className="input-glass font-mono"
                  rows="14"
                  placeholder={`You are a {{agent_role}} assistant.

Your task is to help users with {{task_type}}.

Guidelines:
- Be helpful and professional
- Follow the {{tone}} communication style
- Always prioritize {{priority}}

Context: {{context}}`}
                  required
                />

                {variables.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                    <h3 className="font-bold mb-2 text-sm">Detected Variables ({variables.length})</h3>
                    <div className="flex flex-wrap gap-2">
                      {variables.map((v) => (
                        <span
                          key={v.variable_name}
                          className="px-3 py-1 rounded-full text-sm bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                        >
                          {`{{${v.variable_name}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="card-glass">
                <h2 className="text-xl font-bold mb-2">Environments</h2>
                <p className="text-sm text-gray-400 mb-4">
                  Control which environments your agents can access this instruction from.
                </p>
                
                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <Code className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <div className="font-medium">Development</div>
                        <div className="text-sm text-gray-400">Use <code className="px-1 py-0.5 rounded bg-black/30 text-xs">?env=dev</code> in your API calls to test changes</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={devEnabled}
                      onChange={(e) => setDevEnabled(e.target.checked)}
                      className="toggle"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <div className="font-medium">Production</div>
                        <div className="text-sm text-gray-400">Use <code className="px-1 py-0.5 rounded bg-black/30 text-xs">?env=live</code> for your production agents</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={liveEnabled}
                      onChange={(e) => setLiveEnabled(e.target.checked)}
                      className="toggle"
                    />
                  </label>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div className="card-glass">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold">Configure Variables</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    Set up how each variable behaves when your agents fetch this instruction.
                  </p>
                </div>
                <span className="badge">{variables.length} variables</span>
              </div>

              {variables.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No variables detected.</p>
                  <p className="text-sm">Go back and add {'{{variables}}'} to your template.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {variables.map((variable, index) => {
                    const needsOptions = variable.input_type === 'dropdown' || variable.input_type === 'multiple_choice';
                    return (
                      <div key={variable.variable_name} className="p-5 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-cyan-400 font-mono text-lg">{`{{${variable.variable_name}}}`}</span>
                          <span className="text-xs text-gray-500">Variable {index + 1}</span>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Display Name</label>
                            <input
                              type="text"
                              value={variable.display_name}
                              onChange={(e) => {
                                setVariables((prev) => prev.map((v, i) => 
                                  i === index ? { ...v, display_name: e.target.value } : v
                                ));
                              }}
                              className="input-glass"
                              placeholder="User-friendly name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Input Type</label>
                            <SelectMenu
                              value={variable.input_type}
                              options={INPUT_TYPE_OPTIONS.map((t) => ({
                                value: t.value,
                                label: t.label
                              }))}
                              onChange={(value) => {
                                setVariables((prev) => prev.map((v, i) =>
                                  i === index ? { ...v, input_type: value } : v
                                ));
                              }}
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-2">Description</label>
                          <input
                            type="text"
                            value={variable.description}
                            onChange={(e) => {
                              setVariables((prev) => prev.map((v, i) =>
                                i === index ? { ...v, description: e.target.value } : v
                              ));
                            }}
                            className="input-glass"
                            placeholder="Help text explaining this variable"
                          />
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-2">Default Value</label>
                          <input
                            type="text"
                            value={variable.default_value}
                            onChange={(e) => {
                              setVariables((prev) => prev.map((v, i) =>
                                i === index ? { ...v, default_value: e.target.value } : v
                              ));
                            }}
                            className="input-glass"
                            placeholder="Used if no value is provided"
                          />
                        </div>

                        {/* Dropdown/Multiple Choice Options */}
                        {needsOptions && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium mb-2">
                              {variable.input_type === 'multiple_choice' ? 'Choices (users can select multiple)' : 'Dropdown Options'}
                            </label>
                            <div className="space-y-2">
                              {(variable.dropdown_options || []).map((opt, optIndex) => (
                                <div key={`${variable.variable_name}-opt-${optIndex}`} className="flex gap-2">
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setVariables((prev) => prev.map((v, i) => {
                                        if (i !== index) return v;
                                        const nextOptions = [...(v.dropdown_options || [])];
                                        nextOptions[optIndex] = value;
                                        return { ...v, dropdown_options: nextOptions };
                                      }));
                                    }}
                                    className="input-glass flex-1"
                                    placeholder={`Option ${optIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    className="btn-secondary btn-compact text-red-400 hover:text-red-300"
                                    onClick={() => {
                                      setVariables((prev) => prev.map((v, i) => {
                                        if (i !== index) return v;
                                        const nextOptions = [...(v.dropdown_options || [])];
                                        nextOptions.splice(optIndex, 1);
                                        return { ...v, dropdown_options: nextOptions };
                                      }));
                                    }}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="btn-secondary btn-compact"
                                onClick={() => {
                                  setVariables((prev) => prev.map((v, i) => {
                                    if (i !== index) return v;
                                    const nextOptions = [...(v.dropdown_options || []), ''];
                                    return { ...v, dropdown_options: nextOptions };
                                  }));
                                }}
                              >
                                <Plus className="w-4 h-4" />
                                Add Option
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-4">
            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {step === 1 ? 'Next: Configure Variables' : (loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Instruction'))}
            </button>
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>

        <Modal
          open={downloadModalOpen}
          title="Download Markdown File"
          onClose={() => setDownloadModalOpen(false)}
          footer={(
            <div className="modal-actions">
              <button className="btn-secondary" type="button" onClick={() => setDownloadModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-primary" type="button" onClick={handleDownloadMarkdown}>
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          )}
        >
          <div className="modal-field">
            <label>File Name</label>
            <input
              className="input-glass"
              value={downloadFileName}
              onChange={(event) => setDownloadFileName(event.target.value)}
              placeholder={selectedMarkdownTarget.filename}
            />
            <p className="text-xs text-gray-400 mt-2">{selectedMarkdownTarget.downloadHint}</p>
          </div>
        </Modal>
      </div>
    </div>
  );
}

// Detail view
function AgentInstructionDetail({ instructionId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [instruction, setInstruction] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [rollbackVersion, setRollbackVersion] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  
  // Saved configurations state
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [resolvedPreview, setResolvedPreview] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [configName, setConfigName] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showConfigs, setShowConfigs] = useState(true);
  const [deployments, setDeployments] = useState({ dev: null, live: null });
  const [loadedConfig, setLoadedConfig] = useState(null);
  const [loadedConfigSnapshot, setLoadedConfigSnapshot] = useState({});
  const [deployingEnv, setDeployingEnv] = useState('');
  const [workspaceNames, setWorkspaceNames] = useState({ project: '', folder: '' });

  const loadInstruction = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/agent-instructions/${instructionId}`);
      setInstruction(data);
      setDeployments(data?.deployments || { dev: null, live: null });
      if (data?.project_id || data?.folder_id) {
        const [project, folder] = await Promise.all([
          data?.project_id ? apiClient.get(`/api/instruction-projects/${data.project_id}`).catch(() => null) : Promise.resolve(null),
          data?.folder_id ? apiClient.get(`/api/instruction-folders/${data.folder_id}`).catch(() => null) : Promise.resolve(null)
        ]);
        setWorkspaceNames({
          project: project?.name || '',
          folder: folder?.name || ''
        });
      } else {
        setWorkspaceNames({ project: '', folder: '' });
      }
    } catch (err) {
      setError(err.message || 'Failed to load instruction');
    } finally {
      setLoading(false);
    }
  }, [instructionId]);

  const loadVersions = useCallback(async () => {
    try {
      const data = await apiClient.get(`/api/agent-instructions/${instructionId}/versions`);
      setVersions(data || []);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  }, [instructionId]);

  const loadSavedConfigs = useCallback(async () => {
    try {
      const data = await apiClient.get(`/api/agent-instructions/${instructionId}/configs`);
      setSavedConfigs(data || []);
    } catch (err) {
      console.error('Failed to load saved configs:', err);
    }
  }, [instructionId]);

  useEffect(() => {
    loadInstruction();
    loadVersions();
    loadSavedConfigs();
  }, [loadInstruction, loadSavedConfigs, loadVersions]);

  // Initialize variable values from instruction defaults
  useEffect(() => {
    if (instruction?.variables?.length) {
      const defaults = {};
      instruction.variables.forEach(v => {
        defaults[v.variable_name] = v.default_value || '';
      });
      setVariableValues(defaults);
    }
  }, [instruction]);

  // Update resolved preview when variable values change
  useEffect(() => {
    if (instruction?.instruction_template) {
      let resolved = instruction.instruction_template;
      Object.entries(variableValues).forEach(([name, value]) => {
        resolved = resolved.replace(new RegExp(`\\{\\{${name}\\}\\}`, 'g'), value || '');
      });
      setResolvedPreview(resolved);
    }
  }, [instruction, variableValues]);

  const normalizeValues = useCallback((values = {}) => {
    return Object.keys(values || {})
      .sort()
      .reduce((acc, key) => {
        acc[key] = values[key] ?? '';
        return acc;
      }, {});
  }, []);

  const loadedConfigDirty = useMemo(() => {
    if (!loadedConfig?.config_id) return false;
    const current = JSON.stringify(normalizeValues(variableValues));
    const baseline = JSON.stringify(normalizeValues(loadedConfigSnapshot || {}));
    return current !== baseline;
  }, [loadedConfig, loadedConfigSnapshot, normalizeValues, variableValues]);

  useEffect(() => {
    if (!loadedConfig?.config_id || !savedConfigs.length) return;
    const refreshed = savedConfigs.find((item) => item.config_id === loadedConfig.config_id);
    if (refreshed) {
      setLoadedConfig(refreshed);
    }
  }, [savedConfigs, loadedConfig]);

  useEffect(() => {
    if (loadedConfig?.config_id) return;
    const preferredConfigId = deployments?.dev?.config_id || deployments?.live?.config_id;
    if (!preferredConfigId) return;
    const candidate = savedConfigs.find((item) => item.config_id === preferredConfigId);
    if (!candidate) return;
    setLoadedConfig(candidate);
    setVariableValues(candidate.variable_values || {});
    setLoadedConfigSnapshot(candidate.variable_values || {});
  }, [deployments, loadedConfig, savedConfigs]);

  const handleVariableChange = (varName, value) => {
    setVariableValues(prev => ({ ...prev, [varName]: value }));
  };

  const handleSaveConfig = async () => {
    if (!configName.trim()) {
      setError('Please enter a name for this configuration');
      return;
    }
    setSavingConfig(true);
    setError('');
    try {
      let saved = null;
      if (editingConfig) {
        saved = await apiClient.patch(`/api/agent-instructions/${instructionId}/configs/${editingConfig.config_id}`, {
          name: configName,
          variable_values: variableValues
        });
      } else {
        saved = await apiClient.post(`/api/agent-instructions/${instructionId}/configs`, {
          name: configName,
          variable_values: variableValues
        });
      }
      await loadSavedConfigs();
      if (saved?.config_id) {
        setLoadedConfig(saved);
      } else if (editingConfig?.config_id) {
        setLoadedConfig((prev) => prev && prev.config_id === editingConfig.config_id ? { ...prev, name: configName, variable_values: variableValues } : prev);
      }
      setLoadedConfigSnapshot(variableValues);
      setShowSaveModal(false);
      setConfigName('');
      setEditingConfig(null);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLoadConfig = (config) => {
    const values = config.variable_values || {};
    setVariableValues(values);
    setLoadedConfig(config);
    setLoadedConfigSnapshot(values);
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setConfigName(config.name);
    setVariableValues(config.variable_values || {});
    setLoadedConfig(config);
    setLoadedConfigSnapshot(config.variable_values || {});
    setShowSaveModal(true);
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Delete this saved configuration?')) return;
    try {
      await apiClient.del(`/api/agent-instructions/${instructionId}/configs/${configId}`);
      if (loadedConfig?.config_id === configId) {
        setLoadedConfig(null);
        setLoadedConfigSnapshot({});
      }
      await loadSavedConfigs();
    } catch (err) {
      setError(err.message || 'Failed to delete configuration');
    }
  };

  const copyResolvedInstruction = () => {
    navigator.clipboard.writeText(resolvedPreview);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveLoadedConfigValues = async () => {
    if (!loadedConfig?.config_id) {
      setError('Load a saved config first, or save current values as a new config.');
      return null;
    }
    const updated = await apiClient.patch(`/api/agent-instructions/${instructionId}/configs/${loadedConfig.config_id}`, {
      name: loadedConfig.name,
      variable_values: variableValues
    });
    setLoadedConfig(updated || loadedConfig);
    setLoadedConfigSnapshot(variableValues);
    await loadSavedConfigs();
    return updated || loadedConfig;
  };

  const handleDeployToEnvironment = async (environment, configOverride = null) => {
    setDeployingEnv(environment);
    setError('');
    try {
      let configToDeploy = configOverride || loadedConfig;
      if (!configToDeploy?.config_id) {
        setError('Load a saved configuration before deploying to an environment.');
        return;
      }

      const deployingLoadedConfig = loadedConfig?.config_id && configToDeploy.config_id === loadedConfig.config_id;
      if (deployingLoadedConfig && loadedConfigDirty) {
        configToDeploy = await saveLoadedConfigValues();
      }

      const next = await apiClient.post(`/api/agent-instructions/${instructionId}/deployments`, {
        environment,
        config_id: configToDeploy.config_id
      });
      if (configOverride) {
        setLoadedConfig(configOverride);
        setVariableValues(configOverride.variable_values || {});
        setLoadedConfigSnapshot(configOverride.variable_values || {});
      }
      setDeployments(next || { dev: null, live: null });
      await loadInstruction();
    } catch (err) {
      setError(err.message || `Failed to deploy config to ${environment}`);
    } finally {
      setDeployingEnv('');
    }
  };

  const handleUndeployEnvironment = async (environment) => {
    setDeployingEnv(environment);
    setError('');
    try {
      const next = await apiClient.del(`/api/agent-instructions/${instructionId}/deployments/${environment}`);
      setDeployments(next || { dev: null, live: null });
      await loadInstruction();
    } catch (err) {
      setError(err.message || `Failed to clear ${environment} deployment`);
    } finally {
      setDeployingEnv('');
    }
  };

  const loadVersionDetail = async (versionId) => {
    setLoadingVersion(true);
    setSelectedVersion(versionId);
    try {
      const data = await apiClient.get(`/api/agent-instructions/${instructionId}/versions/${versionId}`);
      setVersionDetail(data);
    } catch (err) {
      console.error('Failed to load version detail:', err);
    } finally {
      setLoadingVersion(false);
    }
  };

  const handlePublish = async (env) => {
    setPublishing(true);
    setError('');
    try {
      await apiClient.post(`/api/agent-instructions/${instructionId}/publish`, {
        environment: env
      });
      await loadInstruction();
      await loadVersions();
    } catch (err) {
      setError(err.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async (env) => {
    setPublishing(true);
    setError('');
    try {
      await apiClient.post(`/api/agent-instructions/${instructionId}/unpublish`, {
        environment: env
      });
      await loadInstruction();
    } catch (err) {
      setError(err.message || 'Failed to unpublish');
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackVersion) return;
    setRollingBack(true);
    setError('');
    try {
      await apiClient.post(`/api/agent-instructions/${instructionId}/rollback`, {
        version_id: rollbackVersion.version_id
      });
      await loadInstruction();
      await loadVersions();
      setShowRollbackConfirm(false);
      setRollbackVersion(null);
      setSelectedVersion(null);
      setVersionDetail(null);
    } catch (err) {
      setError(err.message || 'Failed to rollback');
    } finally {
      setRollingBack(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiClient.del(`/api/agent-instructions/${instructionId}`);
      navigate(backTo);
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  const copyApiExample = () => {
    const example = `curl -H "Authorization: Bearer YOUR_API_KEY" \\
  "${window.location.origin}/api/v1/agent/instructions/${instructionId}?env=live"`;
    navigator.clipboard.writeText(example);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyEndpoint = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/v1/agent/instructions/${instructionId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const backTo = location.state?.backTo || (
    instruction?.project_id && instruction?.folder_id
      ? `/agent-instructions/projects/${instruction.project_id}/folders/${instruction.folder_id}`
      : instruction?.project_id
        ? `/agent-instructions/projects/${instruction.project_id}`
        : '/agent-instructions'
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !instruction) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-2">Instruction unavailable</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button className="btn-primary" onClick={() => navigate(backTo)}>
            Back to Workspace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <div className="mb-6">
        <button
          onClick={() => navigate(backTo)}
          className="text-gray-400 hover:text-white mb-4 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Workspace
        </button>
      </div>

      {error && <div className="error-banner mb-4">{error}</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6" style={{ minWidth: 0 }}>
          <div className="card-glass">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold mb-1">Active Working Config</h2>
                {loadedConfig ? (
                  <>
                    <p className="text-sm text-cyan-300">
                      {loadedConfig.name}
                      {loadedConfigDirty ? <span className="text-yellow-300 ml-2">(unsaved changes)</span> : <span className="text-green-300 ml-2">(saved)</span>}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Config ID: {loadedConfig.config_id}</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">No config loaded yet. Load one from Saved Configurations.</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="btn-secondary btn-compact"
                  onClick={() => {
                    setEditingConfig(null);
                    setConfigName('');
                    setShowSaveModal(true);
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Save As Config
                </button>
                {loadedConfig && (
                  <button
                    className="btn-primary btn-compact"
                    onClick={saveLoadedConfigValues}
                    disabled={!loadedConfigDirty}
                    title={loadedConfigDirty ? 'Save updates to this config' : 'No changes to save'}
                  >
                    Save Loaded Config
                  </button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mt-4">
              {['dev', 'live'].map((env) => {
                const deployed = deployments?.[env];
                const envLabel = env === 'live' ? 'Production (Live)' : 'Development (Dev)';
                return (
                  <div key={env} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium">{envLabel}</div>
                      {deployed ? (
                        <span className="text-xs text-green-300">Config Deployed</span>
                      ) : (
                        <span className="text-xs text-gray-500">No deployed config</span>
                      )}
                    </div>
                    {deployed ? (
                      <div className="text-xs text-gray-400 mt-1">
                        <div>{deployed.config_name || deployed.config_id}</div>
                        {deployed.deployed_at && <div>Deployed: {new Date(deployed.deployed_at).toLocaleString()}</div>}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 mt-1">
                        API calls to this env will use variable defaults until you deploy a config.
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        className="btn-secondary btn-compact flex-1 text-xs"
                        onClick={() => handleDeployToEnvironment(env)}
                        disabled={deployingEnv === env || !loadedConfig}
                        title={loadedConfig ? `Deploy loaded config to ${env}` : 'Load a config first'}
                      >
                        Deploy Loaded Config
                      </button>
                      {deployed && (
                        <button
                          className="btn-secondary btn-compact text-xs text-red-300"
                          onClick={() => handleUndeployEnvironment(env)}
                          disabled={deployingEnv === env}
                          title={`Clear deployed config from ${env}`}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-glass">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{instruction?.name}</h1>
                  <VisibilityIndicator visibility={instruction?.visibility || 'private'} />
                </div>
                {instruction?.description && (
                  <p className="text-gray-400">{instruction.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="icon-action"
                  onClick={() => navigate(`/agent-instructions/${instructionId}/edit`, { state: { backTo } })}
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  className="icon-action danger"
                  onClick={() => setShowDelete(true)}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              {(instruction?.project_id || instruction?.folder_id) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-cyan-500/15 text-cyan-200 border border-cyan-500/20">
                  {workspaceNames.project || 'Workspace'}
                  {workspaceNames.folder ? ` / ${workspaceNames.folder}` : ' linked'}
                </span>
              )}
              {instruction?.source_filename && (
                <span className="instruction-source-pill">
                  <FileText className="w-3 h-3" />
                  {instruction.source_label || 'Imported file'}: {instruction.source_filename}
                </span>
              )}
              {instruction?.live_enabled ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-300 border border-green-500/30">
                  <Globe className="w-3 h-3" />
                  Live
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-500/20 text-gray-400 border border-gray-500/30">
                  <EyeOff className="w-3 h-3" />
                  Live disabled
                </span>
              )}
              {instruction?.dev_enabled ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                  <Code className="w-3 h-3" />
                  Dev
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-500/20 text-gray-400 border border-gray-500/30">
                  <EyeOff className="w-3 h-3" />
                  Dev disabled
                </span>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">INSTRUCTION TEMPLATE</h3>
              <pre className="p-4 rounded-lg bg-black/30 border border-white/10 text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-[400px] overflow-y-auto">
                {instruction?.instruction_template}
              </pre>
            </div>

            {instruction?.variables?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-400 mb-3">VARIABLES ({instruction.variables.length})</h3>
                <div className="grid gap-3">
                  {instruction.variables.map((v) => (
                    <div key={v.variable_name} className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-cyan-400">{`{{${v.variable_name}}}`}</span>
                        <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded">{v.input_type}</span>
                      </div>
                      {v.display_name && v.display_name !== v.variable_name && (
                        <p className="text-sm text-white">{v.display_name}</p>
                      )}
                      {v.description && (
                        <p className="text-sm text-gray-400">{v.description}</p>
                      )}
                      {v.default_value && (
                        <p className="text-xs text-gray-500 mt-1">Default: {v.default_value}</p>
                      )}
                      {v.dropdown_options?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {v.dropdown_options.map((opt, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/10 text-gray-300">{opt}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* API Usage */}
          <div className="card-glass">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              API Usage
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Keep one stable endpoint in your agent. The API returns the resolved instruction using the config deployed to that environment for your account.
            </p>
            
            <div className="mb-4 p-3 rounded-lg bg-black/40 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase">Endpoint</span>
                <button className="text-xs text-cyan-400 hover:text-cyan-300" onClick={copyEndpoint}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <code className="text-sm text-cyan-300 break-all">
                GET /api/v1/agent/instructions/{instructionId}
              </code>
              <div className="mt-2 text-xs text-gray-400">
                Dev config: {deployments?.dev?.config_name || 'None deployed'} | Live config: {deployments?.live?.config_name || 'None deployed'}
              </div>
            </div>

            <div className="relative">
              <pre className="p-4 rounded-lg bg-black/40 border border-white/10 text-sm font-mono overflow-x-auto">
{`# Fetch resolved instruction for development/testing
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  "${window.location.origin}/api/v1/agent/instructions/${instructionId}?env=dev"

# Fetch resolved instruction for production
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  "${window.location.origin}/api/v1/agent/instructions/${instructionId}?env=live"

# Optional: override specific values at runtime
curl -X POST -H "Authorization: Bearer YOUR_API_KEY" -H "Content-Type: application/json" \\
  -d '{"variable_values":{"tone":"concise"}}' \\
  "${window.location.origin}/api/v1/agent/instructions/${instructionId}/resolve?env=live"`}
              </pre>
              <button
                className="absolute top-2 right-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                onClick={copyApiExample}
                title="Copy example"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <p className="text-sm text-cyan-300">
                <strong>How it works:</strong> Publish instruction versions to <code className="px-1 py-0.5 rounded bg-black/30">dev/live</code>, deploy a saved config to each environment, then keep your agent integration URL unchanged.
              </p>
            </div>
          </div>

          {/* Use This Instruction - Variable Input Form */}
          {instruction?.variables?.length > 0 && (
            <div className="card-glass">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-400" />
                  Config Editor
                </h2>
                <button
                  className="btn-primary btn-compact"
                  onClick={() => {
                    setEditingConfig(null);
                    setConfigName('');
                    setShowSaveModal(true);
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Save Config
                </button>
              </div>
              <p className="text-sm text-gray-400 mb-6">
                Edit variable values, then save/load configs and deploy one config per environment. Your API integrations stay on the same instruction endpoint.
              </p>

              {/* Variable Input Form */}
              <div className="space-y-4 mb-6">
                {instruction.variables.map((v) => (
                  <div key={v.variable_name} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <label className="block mb-2">
                      <span className="font-medium text-white">{v.display_name || v.variable_name}</span>
                      {v.is_required && <span className="text-red-400 ml-1">*</span>}
                      <span className="text-xs text-gray-500 ml-2 font-mono">{`{{${v.variable_name}}}`}</span>
                    </label>
                    {v.description && (
                      <p className="text-xs text-gray-400 mb-2">{v.description}</p>
                    )}
                    
                    {v.input_type === 'dropdown' ? (
                      <select
                        className="input-glass w-full"
                        value={variableValues[v.variable_name] || ''}
                        onChange={(e) => handleVariableChange(v.variable_name, e.target.value)}
                      >
                        <option value="">Select...</option>
                        {(v.dropdown_options || []).map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : v.input_type === 'multiple_choice' ? (
                      <div className="flex flex-wrap gap-2">
                        {(v.dropdown_options || []).map((opt, i) => {
                          const selected = (variableValues[v.variable_name] || '').split(',').map(s => s.trim()).includes(opt);
                          return (
                            <button
                              key={i}
                              type="button"
                              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                                selected
                                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                              }`}
                              onClick={() => {
                                const current = (variableValues[v.variable_name] || '').split(',').map(s => s.trim()).filter(Boolean);
                                const next = selected
                                  ? current.filter(x => x !== opt)
                                  : [...current, opt];
                                handleVariableChange(v.variable_name, next.join(', '));
                              }}
                            >
                              {selected && <Check className="w-3 h-3 inline mr-1" />}
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    ) : v.input_type === 'multi_text' ? (
                      <textarea
                        className="input-glass w-full"
                        rows="3"
                        value={variableValues[v.variable_name] || ''}
                        onChange={(e) => handleVariableChange(v.variable_name, e.target.value)}
                        placeholder={v.default_value || `Enter ${v.display_name || v.variable_name}...`}
                      />
                    ) : v.input_type === 'number' ? (
                      <input
                        type="number"
                        className="input-glass w-full"
                        value={variableValues[v.variable_name] || ''}
                        onChange={(e) => handleVariableChange(v.variable_name, e.target.value)}
                        placeholder={v.default_value || '0'}
                      />
                    ) : (
                      <input
                        type="text"
                        className="input-glass w-full"
                        value={variableValues[v.variable_name] || ''}
                        onChange={(e) => handleVariableChange(v.variable_name, e.target.value)}
                        placeholder={v.default_value || `Enter ${v.display_name || v.variable_name}...`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Resolved Preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-400">RESOLVED PREVIEW</h3>
                  <button
                    className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    onClick={copyResolvedInstruction}
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="p-4 rounded-lg bg-black/30 border border-white/10 text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-[300px] overflow-y-auto">
                  {resolvedPreview || '(Fill in variables above to see preview)'}
                </pre>
              </div>
            </div>
          )}

          {/* Saved Configurations */}
          <div className="card-glass">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setShowConfigs(!showConfigs)}
            >
              <h3 className="font-bold flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Saved Configurations
                {savedConfigs.length > 0 && (
                  <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{savedConfigs.length}</span>
                )}
              </h3>
              {showConfigs ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            
            {showConfigs && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-4">
                  Your saved variable configurations. Only you can see these.
                </p>
                
                {savedConfigs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No saved configurations yet. Fill in variables above and click "Save Config".
                  </p>
                ) : (
                  <div className="space-y-2">
                    {savedConfigs.map((config) => (
                      <div
                        key={config.config_id}
                        className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-white">
                            {config.name}
                            {loadedConfig?.config_id === config.config_id && (
                              <span className="ml-2 text-xs text-cyan-300">(loaded)</span>
                            )}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              className="p-1 text-gray-400 hover:text-cyan-400"
                              onClick={() => handleLoadConfig(config)}
                              title="Load this config"
                            >
                              <Zap className="w-3 h-3" />
                            </button>
                            <button
                              className="p-1 text-gray-400 hover:text-white"
                              onClick={() => handleEditConfig(config)}
                              title="Edit"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              className="p-1 text-gray-400 hover:text-red-400"
                              onClick={() => handleDeleteConfig(config.config_id)}
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {Object.keys(config.variable_values || {}).length} variables | Created {new Date(config.created_at).toLocaleDateString()}
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            className="btn-secondary btn-compact text-xs"
                            onClick={() => handleDeployToEnvironment('dev', config)}
                          >
                            Deploy Dev
                          </button>
                          <button
                            className="btn-secondary btn-compact text-xs"
                            onClick={() => handleDeployToEnvironment('live', config)}
                          >
                            Deploy Live
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" style={{ minWidth: 0 }}>
          {/* Environment Controls */}
          <div className="card-glass">
            <h3 className="font-bold mb-4">Environment Controls</h3>
            <div className="space-y-3">
              {/* Dev controls */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-yellow-400" />
                    <span className="font-medium">Development</span>
                  </div>
                  {instruction?.dev_enabled ? (
                    <span className="text-xs text-green-400">Active</span>
                  ) : (
                    <span className="text-xs text-gray-500">Disabled</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Deployed config: <span className="text-gray-300">{deployments?.dev?.config_name || 'None'}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary btn-compact flex-1 text-xs"
                    onClick={() => handlePublish('dev')}
                    disabled={publishing}
                  >
                    Publish Instruction to Dev
                  </button>
                  {instruction?.dev_enabled && (
                    <button
                      className="btn-secondary btn-compact text-xs text-red-400"
                      onClick={() => handleUnpublish('dev')}
                      disabled={publishing}
                      title="Disable dev access"
                    >
                      <EyeOff className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Live controls */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-green-400" />
                    <span className="font-medium">Production</span>
                  </div>
                  {instruction?.live_enabled ? (
                    <span className="text-xs text-green-400">Active</span>
                  ) : (
                    <span className="text-xs text-gray-500">Disabled</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  Deployed config: <span className="text-gray-300">{deployments?.live?.config_name || 'None'}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-primary btn-compact flex-1 text-xs"
                    onClick={() => handlePublish('live')}
                    disabled={publishing}
                  >
                    <Globe className="w-3 h-3" />
                    Publish Instruction to Live
                  </button>
                  {instruction?.live_enabled && (
                    <button
                      className="btn-secondary btn-compact text-xs text-red-400"
                      onClick={() => handleUnpublish('live')}
                      disabled={publishing}
                      title="Disable live access"
                    >
                      <EyeOff className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Version history */}
          <div className="card-glass">
            <button
              className="w-full flex items-center justify-between"
              onClick={() => setShowVersions(!showVersions)}
            >
              <h3 className="font-bold flex items-center gap-2">
                <History className="w-4 h-4" />
                Version History
              </h3>
              {showVersions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            
            {showVersions && (
              <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                {versions.length === 0 ? (
                  <p className="text-sm text-gray-400">No versions yet. Publish to create a version.</p>
                ) : (
                  versions.map((v, idx) => (
                    <div 
                      key={v.version_id} 
                      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedVersion === v.version_id 
                          ? 'bg-cyan-500/10 border-cyan-500/30' 
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => loadVersionDetail(v.version_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          v.environment === 'live' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {v.environment}
                        </span>
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-xs text-cyan-400">Latest</span>}
                          <span className="text-gray-400 text-xs">
                            {new Date(v.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 truncate flex-1">{v.name}</span>
                        {selectedVersion === v.version_id && idx > 0 && (
                          <button
                            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRollbackVersion(v);
                              setShowRollbackConfirm(true);
                            }}
                          >
                            <RotateCcw className="w-3 h-3" />
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Version detail preview */}
            {selectedVersion && versionDetail && (
              <div className="mt-4 p-3 rounded-lg bg-black/30 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium">Version Preview</h4>
                  <button
                    className="text-xs text-gray-400 hover:text-white"
                    onClick={() => {
                      setSelectedVersion(null);
                      setVersionDetail(null);
                    }}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                {loadingVersion ? (
                  <div className="text-center py-4">
                    <div className="spinner-small"></div>
                  </div>
                ) : (
                  <pre className="text-xs font-mono text-gray-400 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {versionDetail.instruction_template}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete modal */}
      <Modal
        open={showDelete}
        title="Delete Instruction"
        onClose={() => setShowDelete(false)}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setShowDelete(false)}>
              Cancel
            </button>
            <button className="btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete <strong>{instruction?.name}</strong>?</p>
        <p className="text-sm text-gray-400 mt-2">This will disable all API access immediately. This action cannot be undone.</p>
      </Modal>

      {/* Rollback confirmation modal */}
      <Modal
        open={showRollbackConfirm}
        title="Restore Version"
        onClose={() => {
          setShowRollbackConfirm(false);
          setRollbackVersion(null);
        }}
        footer={
          <>
            <button className="btn-secondary" onClick={() => {
              setShowRollbackConfirm(false);
              setRollbackVersion(null);
            }}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleRollback} disabled={rollingBack}>
              {rollingBack ? 'Restoring...' : 'Restore This Version'}
            </button>
          </>
        }
      >
        <p>This will restore your instruction to the version from <strong>{rollbackVersion && new Date(rollbackVersion.created_at).toLocaleString()}</strong>.</p>
        <p className="text-sm text-gray-400 mt-2">Your current content will be saved as a new version before restoring.</p>
      </Modal>

      {/* Save Configuration modal */}
      <Modal
        open={showSaveModal}
        title={editingConfig ? 'Update Configuration' : 'Save Configuration'}
        onClose={() => {
          setShowSaveModal(false);
          setConfigName('');
          setEditingConfig(null);
        }}
        footer={
          <>
            <button className="btn-secondary" onClick={() => {
              setShowSaveModal(false);
              setConfigName('');
              setEditingConfig(null);
            }}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSaveConfig} disabled={savingConfig}>
              {savingConfig ? 'Saving...' : (editingConfig ? 'Update' : 'Save Configuration')}
            </button>
          </>
        }
      >
        <p className="text-sm text-gray-400 mb-4">
          Save these variable values as a reusable configuration, then deploy it to dev/live from this page.
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Configuration Name *</label>
          <input
            type="text"
            className="input-glass w-full"
            value={configName}
            onChange={(e) => setConfigName(e.target.value)}
            placeholder="e.g., Production Settings, Test Config..."
          />
        </div>
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <h4 className="text-xs font-medium text-gray-400 mb-2">VARIABLE VALUES TO SAVE</h4>
          <div className="space-y-1 text-sm">
            {Object.entries(variableValues).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400 font-mono">{key}:</span>
                <span className="text-white truncate max-w-[200px]">{value || '(empty)'}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Main router component
export default function AgentInstructionsPage() {
  const { instructionId } = useParams();
  const location = useLocation();

  if (location.pathname === '/agent-instructions/create') {
    return <AgentInstructionForm />;
  }

  if (instructionId && location.pathname.endsWith('/edit')) {
    return <AgentInstructionForm instructionId={instructionId} />;
  }

  if (instructionId) {
    return <AgentInstructionDetail instructionId={instructionId} />;
  }

  return <AgentInstructionsList />;
}

